'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Action, Loadout } from '@cs-shot/battle-sim';
import { isPartySnapshot } from '@cs-shot/battle-sim';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import { useTelegramBackButton } from '../../../hooks/useTelegramBackButton';
import translations from './RushMid.translations';
import { useRawInitDataWithDev } from '../../../hooks/useRawInitDataWithDev';
import { useRuntime } from '../../../runtime/RuntimeContext';
import { useNavigation } from '../../../contexts/NavigationContext';
import { extractStartParam } from '../../../utils/telegram';
import { usePartyShare } from '../../../hooks/usePartyShare';
import { useRushMidMatch, type Side, type SidePref } from './useRushMidMatch';
import { useParty } from './useParty';
import { useBattlePreload } from './useBattlePreload';
import { useRushMidSkins } from '../../../queries/useRushMidQueries';
import { useRushMidAudio } from './useRushMidAudio';
import { useSnapshotSync } from './useSnapshotSync';
import { useApiClient } from '../../../contexts/ApiContext';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../../lib/queryClient';
import { apiErrorCode, formatApiError } from '../../../utils/apiError';
import { rushMidErrorKey } from './rushMidErrors';
import { JoinMatchmakingRequestSideEnum as SideEnum, JoinMatchmakingRequestMatchModeEnum as ModeEnum } from '../../../services/generated/Api';

export type RushMidTab = 'games' | 'fighter' | 'history' | 'rating' | 'raffle';

const normalizeTab = (t?: string): RushMidTab =>
  t === 'fighter' || t === 'history' || t === 'rating' || t === 'raffle' ? t : 'games';


export function useRushMidController(initialTab?: string, initialCreate?: boolean) {
  const { t, lang } = useLocalTranslation(translations);
  const mm = useRushMidMatch();
  const party = useParty();
  const navigation = useNavigation();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const { snapshot, status, apply, setSnapshot, generation } = useSnapshotSync(mm);
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const { assetsReady: spritesReady, loadProgress, loadError, phraseIdx } = useBattlePreload();
  const { isLoading: skinsLoading } = useRushMidSkins();
  const assetsReady = spritesReady && !skinsLoading;
  const partyMembers = snapshot && isPartySnapshot(snapshot) ? snapshot.members.length : null;
  useRushMidAudio(assetsReady, status, partyMembers);

  const resolveErrorText = useCallback((err: unknown, def: string): string => {
    const key = rushMidErrorKey(err);
    if (key) return t(key);
    console.error('Rush Mid command failed:', formatApiError(err));
    return def;
  }, [t]);

  const [tab, setTab] = useState<RushMidTab>(normalizeTab(initialTab));
  const [finished, setFinished] = useState(false);
  const [pendingCreate, setPendingCreate] = useState(!!initialCreate);
  const requestCreate = useCallback(() => setPendingCreate(true), []);
  const consumePendingCreate = useCallback(() => setPendingCreate(false), []);
  const [runtimeError, setRuntimeError] = useState<string | null>(null);
  const [runtimeErrorCode, setRuntimeErrorCode] = useState<string | undefined>(undefined);
  const failRuntime = useCallback((err: unknown, def: string) => {
    setRuntimeError(resolveErrorText(err, def));
    setRuntimeErrorCode(apiErrorCode(err));
  }, [resolveErrorText]);
  const [leavingResult, setLeavingResult] = useState(false);

  useTelegramBackButton({ isDisabled: status === 'OPEN' || status === 'BUYING' || status === 'PLAYING' });

  // WHY: маркер «уже вступил» в sessionStorage — start_param живёт всю TMA-сессию, ref сбрасывается на remount и вступал бы
  // снова
  const runtime = useRuntime();
  const rawInitData = useRawInitDataWithDev();
  const partyLinkRef = useRef(false);
  useEffect(() => {
    if (partyLinkRef.current) return;
    if (runtime === 'tma' && rawInitData === undefined) return;
    partyLinkRef.current = true;
    const fromUrl = new URLSearchParams(window.location.search).get('party');
    const sp = extractStartParam(rawInitData);
    const fromTma = sp && sp.startsWith('party_') ? sp.slice('party_'.length) : null;
    const code = (fromUrl ?? fromTma ?? '').toUpperCase();
    if (code.length < 4) return;
    const consumedKey = `rushmid:party-link:${code}`;
    try {
      if (sessionStorage.getItem(consumedKey)) return;
      sessionStorage.setItem(consumedKey, '1');
    } catch { }
    if (fromUrl) {
      const url = new URL(window.location.href);
      url.searchParams.delete('party');
      window.history.replaceState(null, '', url);
    }
    mm.getSnapshot()
      .then((s) => {
        if (s.status !== 'NONE' && s.status !== 'PARTY' && s.status !== 'OVER') { apply(s); return; }
        return party.joinParty(code).then((p) => setSnapshot(p)).catch((e) => failRuntime(e, 'Не удалось войти в пати'));
      })
      .catch(() => party.joinParty(code).then((p) => setSnapshot(p)).catch((e) => failRuntime(e, 'Не удалось войти в пати')));
  }, [runtime, rawInitData, party, mm, apply, setSnapshot, failRuntime]);

  const viewGame = useCallback((id: string) => {
    setRuntimeError(null);
    setTab('games');
    navigation.updateCurrentParams({ tab: 'games' });
    navigation.navigate('rush-mid-open-game', { gameId: id });
  }, [navigation]);


  const gameLinkRef = useRef(false);
  useEffect(() => {
    if (gameLinkRef.current) return;
    if (runtime === 'tma' && rawInitData === undefined) return;
    gameLinkRef.current = true;
    const fromUrl = new URLSearchParams(window.location.search).get('game');
    const sp = extractStartParam(rawInitData);
    const fromTma = sp && sp.startsWith('game_') ? sp.slice('game_'.length) : null;
    const id = fromUrl ?? fromTma ?? '';
    if (!id) return;
    const consumedKey = `rushmid:game-link:${id}`;
    try {
      if (sessionStorage.getItem(consumedKey)) return;
      sessionStorage.setItem(consumedKey, '1');
    } catch { }
    if (fromUrl) {
      const url = new URL(window.location.href);
      url.searchParams.delete('game');
      window.history.replaceState(null, '', url);
    }
    viewGame(id);
  }, [runtime, rawInitData, viewGame]);


  const matchId =
    snapshot && snapshot.status !== 'NONE' && snapshot.status !== 'PARTY'
      ? snapshot.matchId
      : '';

  useEffect(() => { setFinished(false); }, [matchId]);

  const saveSoloConfig = useCallback((mode: number, ticket: number, side: Side, matchMode: 'ranked' | 'bots') => {
    const sideEnum = side === 't' ? SideEnum.T : SideEnum.Ct;
    const modeEnum = matchMode === 'bots' ? ModeEnum.Bots : ModeEnum.Ranked;
    apiClient.api.rushMidControllerSaveLobbyConfig({ ticket, mode, side: sideEnum, matchMode: modeEnum }).catch(() => {});
    queryClient.setQueryData(queryKeys.rushMidSkins, (old: unknown) =>
      old && typeof old === 'object' ? { ...old, ticket, mode, side, matchMode } : old);
  }, [apiClient, queryClient]);

  const create = useCallback((mode: number, ticket: number, side: Side, matchMode: 'ranked' | 'bots') => {
    setRuntimeError(null);
    setFinished(false);
    if (snapshot && snapshot.status === 'PARTY') {
      const p = snapshot;
      const changed = p.mode !== mode || p.ticket !== ticket || p.side !== side || (p.matchMode ?? 'ranked') !== matchMode;
      const needsReconfirm = changed && p.members.length >= 2;
      const flow = needsReconfirm
        ? party.configParty(mode, ticket, side, matchMode)
        : party.configParty(mode, ticket, side, matchMode).then(() => mm.createGame(mode, ticket, side, matchMode));
      flow.then(apply).catch((err) => failRuntime(err, 'Не удалось создать игру'));
      return;
    }
    saveSoloConfig(mode, ticket, side, matchMode);
    mm.createGame(mode, ticket, side, matchMode)
      .then(apply)
      .catch((err) => failRuntime(err, 'Не удалось создать игру'));
  }, [mm, party, snapshot, apply, failRuntime, saveSoloConfig]);

  const leaveGame = useCallback(() => {
    setRuntimeError(null);
    mm.leaveGame()
      .then((s) => setSnapshot(s))
      .catch(() => {
        mm.getSnapshot().then((s) => setSnapshot(s)).catch(() => {});
      });
  }, [mm, setSnapshot]);

  const fail = useCallback((def: string) => (err: unknown) => failRuntime(err, def), [failRuntime]);
  const joinPartyByCode = useCallback((code: string) => {
    setRuntimeError(null);
    party.joinParty(code).then(apply).catch(fail('Не удалось войти в пати'));
  }, [party, apply, fail]);
  const partyConfig = useCallback((m: number, tk: number, s: SidePref, mm: 'ranked' | 'bots') => {
    party.configParty(m, tk, s, mm).then(apply).catch(fail('Не удалось изменить настройки пати'));
  }, [party, apply, fail]);
  const partyLeave = useCallback(() => {
    party.leaveParty().then(apply).catch(() => {
      mm.getSnapshot().then((s) => setSnapshot(s)).catch(() => {});
    });
  }, [party, mm, apply, setSnapshot]);
  const partyKick = useCallback((targetUserId: string) => {
    party.kickParty(targetUserId).then(apply).catch(fail('Не удалось исключить игрока'));
  }, [party, apply, fail]);
  const createParty = useCallback(() => {
    setRuntimeError(null);
    party.createParty().then(apply).catch(fail('Не удалось создать пати'));
  }, [party, apply, fail]);
  const partyReady = useCallback((r: boolean) => {
    setRuntimeError(null);
    party.setReady(r).then(apply).catch(fail('Не удалось изменить готовность'));
  }, [party, apply, fail]);
  const partyCode = snapshot?.status === 'PARTY' ? snapshot.code : '';
  const partyShare = usePartyShare(partyCode, t('partyShareText', { code: partyCode }), t('partyJoinAction'));

  const ready = useCallback((loadout: Loadout) => {
    setRuntimeError(null);
    if (!matchId) return;
    mm.submitLoadout(matchId, loadout, true).then(apply).catch((err) => {
      console.warn('[rush-mid] loadout submit failed (non-fatal)', err);
    });
  }, [mm, apply, matchId]);

  const saveLoadout = useCallback((loadout: Loadout) => {
    if (!matchId) return;
    mm.submitLoadout(matchId, loadout, false).catch(() => {});
  }, [mm, matchId]);

  const onAction = useCallback(
    (action: Action, round: number) => {
      if (!matchId) return;
      const s = snapshotRef.current;
      if (s?.status === 'PLAYING') {
        if (s.round !== round || s.yourActionSubmitted) return;
        const you = s.fighters.find((f) => f.id === s.yourFighterId);
        if (you && !you.alive) return;
      }
      mm.submitAction(matchId, action, round).then(apply).catch((err) => {
        console.warn('[rush-mid] action submit failed (non-fatal)', err);
      });
    },
    [mm, apply, matchId],
  );

  const finish = useCallback(() => setFinished(true), []);
  const again = useCallback(() => {
    setRuntimeError(null);
    setTab('games');
    const id = snapshot?.status === 'OVER' ? matchId : '';
    setLeavingResult(true);
    const gen = generation();
    const dismiss = id ? mm.leaveMatch(id).catch(() => {}) : Promise.resolve();
    dismiss
      .then(async () => {
        for (let i = 0; i < 5; i++) {
          const s = await mm.getSnapshot().catch(() => null);
          if (generation() !== gen) return;
          if (s && s.status !== 'OVER' && s.status !== 'NONE') { setSnapshot(s); return; }
          await new Promise((r) => setTimeout(r, 250));
        }
        if (generation() !== gen) return;
        setSnapshot(null);
      })
      .finally(() => setLeavingResult(false));
  }, [mm, matchId, snapshot, setSnapshot, generation]);
  const topUp = useCallback(() => { navigation.navigate('payment'); }, [navigation]);

  const switchTab = useCallback((next: RushMidTab) => {
    setRuntimeError(null);
    setTab(next);
    navigation.updateCurrentParams({ tab: next });
  }, [navigation]);
  const openMatch = useCallback((id: string, view?: 'result' | 'log') => {
    navigation.navigate('rush-mid-match-detail', { matchId: id, view: view ?? 'result' });
  }, [navigation]);
  const openSpectate = useCallback((id: string) => {
    navigation.navigate('rush-mid-spectate', { matchId: id });
  }, [navigation]);

  return {
    t, lang,
    snapshot, status, matchId, finished, leavingResult,
    assetsReady, loadProgress, loadError, phraseIdx,
    runtimeError, runtimeErrorCode, topUp,
    tab,
    pendingCreate, requestCreate, consumePendingCreate,
    create, leaveGame, viewGame,
    createParty, joinPartyByCode, partyConfig, partyReady, partyLeave, partyKick, partyShare,
    ready, saveLoadout, onAction, finish, again,
    switchTab, openMatch, openSpectate,
  };
}

export type RushMidController = ReturnType<typeof useRushMidController>;
